/**
 * Submitting a long text as a book: one video per chapter, each approved on its own
 * (engine app/routers/books_api.py). Text only for now; files still go through the
 * single-video path.
 *
 * Here rather than beside the library's booksApi because these are submissions, and
 * because they resolve their settings the way every other submission does
 * (generationDefaults): the seconds-per-scene sent decides how many words fit one
 * video, so a book must be split with the same value it is then priced with.
 */
import { postJson } from '@/shared/api/apiClient';
import { resolveDurationSeconds, resolveModelName } from '@/features/generation/utils/generationDefaults';

const BOOKS_PATH = '/api/v1/books';

type PreviewApiItem = {
  word_count: number;
  word_limit: number;
  book_suggested: boolean;
  video_count: number;
  chapters: {
    chapter_index: number;
    part_index: number;
    chapter_title?: string | null;
    word_count: number;
  }[];
};

export type BookPreviewChapter = {
  chapterIndex: number;
  partIndex: number;
  chapterTitle?: string;
  wordCount: number;
};

export type BookPreview = {
  wordCount: number;
  /** The most words one video can take. */
  wordLimit: number;
  /** The text is too long for one video, so it should be offered as a book. */
  bookSuggested: boolean;
  videoCount: number;
  chapters: BookPreviewChapter[];
};

/** Settings shared by every chapter, chosen the way a single video's are. */
export type BookSettings = {
  modelName?: string;
  duration?: number;
  aspectRatio?: string;
  style?: string;
  mode?: string;
};

function settingsFields(settings: BookSettings) {
  const modelName = resolveModelName(settings.modelName);
  return {
    model_name: modelName,
    duration: resolveDurationSeconds(modelName, settings.duration),
    ...(settings.aspectRatio ? { aspect_ratio: settings.aspectRatio } : {}),
    ...(settings.style ? { style: settings.style } : {}),
    ...(settings.mode ? { mode: settings.mode } : {}),
  };
}

/** How the text would split. Free: no model call, nothing stored. */
export async function previewBook(
  text: string,
  settings: BookSettings = {},
  options: { signal?: AbortSignal } = {},
): Promise<BookPreview> {
  const { duration } = settingsFields(settings);
  const item = await postJson<PreviewApiItem>(
    `${BOOKS_PATH}/preview`,
    { text_input: text, duration },
    { signal: options.signal },
  );
  return {
    wordCount: item.word_count,
    wordLimit: item.word_limit,
    bookSuggested: item.book_suggested,
    videoCount: item.video_count,
    chapters: item.chapters.map((chapter) => ({
      chapterIndex: chapter.chapter_index,
      partIndex: chapter.part_index,
      chapterTitle: chapter.chapter_title ?? undefined,
      wordCount: chapter.word_count,
    })),
  };
}

export type CreatedBook = { bookId: string; jobId: string; videoCount: number };

/** Create the book and start its first chapter, which then awaits approval like any video. */
export async function createBook(
  text: string,
  title: string | undefined,
  settings: BookSettings = {},
): Promise<CreatedBook> {
  const response = await postJson<{ book_id: string; job_id: string; video_count: number }>(BOOKS_PATH, {
    text_input: text,
    ...(title ? { title } : {}),
    ...settingsFields(settings),
  });
  return { bookId: response.book_id, jobId: response.job_id, videoCount: response.video_count };
}
